import { trpc } from './trpc';

/**
 * Hook to get distinct browsers for filtering
 */
export function useBrowserFilterOptions() {
  const { data, isLoading, error } = trpc.customers.list.useQuery(
    {
      pagination: { page: 1, pageSize: 1 },
    },
    {
      select: (data) => {
        const browsers = (data.options as any)?.browsers || [];
        console.log('[useBrowserFilterOptions] Raw browsers:', browsers);
        const mapped = browsers.map((browser: string) => ({
          label: browser,
          value: browser,
        }));
        console.log('[useBrowserFilterOptions] Mapped options:', mapped);
        return mapped;
      },
    },
  );

  console.log('[useBrowserFilterOptions] Returning:', { options: data, loading: isLoading, error: !!error });

  return {
    options: data || [],
    loading: isLoading,
    error: !!error,
  };
}

/**
 * Hook to get distinct operating systems for filtering
 */
export function useOperatingSystemFilterOptions() {
  const { data, isLoading, error } = trpc.customers.list.useQuery(
    {
      pagination: { page: 1, pageSize: 1 },
    },
    {
      select: (data) => {
        const oses = (data.options as any)?.operatingSystems || [];
        console.log('[useOperatingSystemFilterOptions] Raw OSes:', oses);
        const mapped = oses.map((os: string) => ({
          label: os,
          value: os,
        }));
        console.log('[useOperatingSystemFilterOptions] Mapped options:', mapped);
        return mapped;
      },
    },
  );

  console.log('[useOperatingSystemFilterOptions] Returning:', { options: data, loading: isLoading, error: !!error });

  return {
    options: data || [],
    loading: isLoading,
    error: !!error,
  };
}

/**
 * Hook to get distinct device types for filtering
 */
export function useDeviceTypeFilterOptions() {
  const { data, isLoading, error } = trpc.customers.list.useQuery(
    {
      pagination: { page: 1, pageSize: 1 },
    },
    {
      select: (data) => {
        const types = (data.options as any)?.deviceTypes || [];
        console.log('[useDeviceTypeFilterOptions] Raw types:', types);
        const mapped = types.map((type: string) => ({
          label: type,
          value: type,
        }));
        console.log('[useDeviceTypeFilterOptions] Mapped options:', mapped);
        return mapped;
      },
    },
  );

  console.log('[useDeviceTypeFilterOptions] Returning:', { options: data, loading: isLoading, error: !!error });

  return {
    options: data || [],
    loading: isLoading,
    error: !!error,
  };
}

/**
 * Hook to get distinct device models for filtering
 */
export function useDeviceModelFilterOptions() {
  const { data, isLoading, error } = trpc.customers.list.useQuery(
    {
      pagination: { page: 1, pageSize: 1 },
    },
    {
      select: (data) => {
        const models = (data.options as any)?.deviceModels || [];
        console.log('[useDeviceModelFilterOptions] Raw models:', models);
        const mapped = models.map((model: string) => ({
          label: model,
          value: model,
        }));
        console.log('[useDeviceModelFilterOptions] Mapped options:', mapped);
        return mapped;
      },
    },
  );

  console.log('[useDeviceModelFilterOptions] Returning:', { options: data, loading: isLoading, error: !!error });

  return {
    options: data || [],
    loading: isLoading,
    error: !!error,
  };
}

/**
 */

/**
 * Hook to get distinct countries for filtering
 */
export function useCountryFilterOptions() {
  const { data, isLoading, error } = trpc.customers.list.useQuery(
    {
      pagination: { page: 1, pageSize: 1 },
    },
    {
      select: (data) => {
        const countries = (data.options as any)?.countryCodes || [];
        console.log('[useCountryFilterOptions] Raw countries:', countries);
        const mapped = countries.map((country: { id: string; name: string }) => ({
          label: country.name,
          value: country.id,
        }));
        console.log('[useCountryFilterOptions] Mapped options:', mapped);
        return mapped;
      },
    },
  );

  console.log('[useCountryFilterOptions] Returning:', { options: data, loading: isLoading, error: !!error });

  return {
    options: data || [],
    loading: isLoading,
    error: !!error,
  };
}

/**
 * Format a tag for display (e.g., "source:calendly" -> "Source: Calendly")
 */
function formatTagLabel(tag: string): string {
  if (!tag.includes(':')) {
    // If no colon, just capitalize first letter
    return tag.charAt(0).toUpperCase() + tag.slice(1);
  }

  const [prefix, ...valueParts] = tag.split(':');
  const value = valueParts.join(':'); // Handle cases with multiple colons

  // Format prefix: replace underscores with spaces and capitalize
  const formattedPrefix = prefix
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  // Format value: capitalize first letter, keep rest as-is
  const formattedValue = value.charAt(0).toUpperCase() + value.slice(1);

  return `${formattedPrefix}: ${formattedValue}`;
}

/**
 * Hook to get distinct tags for filtering
 */
export function useTagsFilterOptions() {
  const { data, isLoading, error } = trpc.customers.list.useQuery(
    {
      pagination: { page: 1, pageSize: 1 },
    },
    {
      select: (data) => {
        const tags = (data.options as any)?.tags || [];
        return tags.map((tag: string) => ({
          label: formatTagLabel(tag),
          value: tag,
        }));
      },
    },
  );

  return {
    options: data || [],
    loading: isLoading,
    error: !!error,
  };
}

/**
 * Hook to get distinct continents from customer tags
 * Extracts continents from geo_continent:XX tags
 */
export function useContinentFilterOptions() {
  const { data, isLoading, error } = trpc.customers.list.useQuery(
    {
      pagination: { page: 1, pageSize: 1 },
    },
    {
      select: (data) => {
        const tags = (data.options as any)?.tags || [];
        // Extract continents from geo_continent:XX tags
        const continents = new Set<string>();
        tags.forEach((tag: string) => {
          if (tag.startsWith('geo_continent:')) {
            const code = tag.split(':')[1];
            if (code) continents.add(code);
          }
        });

        // Map to full names
        const continentMap: Record<string, string> = {
          'EU': 'Europe',
          'NA': 'North America',
          'SA': 'South America',
          'AS': 'Asia',
          'AF': 'Africa',
          'OC': 'Oceania',
          'AN': 'Antarctica',
        };

        return Array.from(continents)
          .sort()
          .map((code) => ({
            label: continentMap[code] || code,
            value: code,
          }));
      },
    },
  );

  return {
    options: data || [],
    loading: isLoading,
    error: !!error,
  };
}

/**
 * Hook to get distinct locales from customer tags
 * Extracts locales from browser_locale:XX tags
 */
export function useLocaleFilterOptions() {
  const { data, isLoading, error } = trpc.customers.list.useQuery(
    {
      pagination: { page: 1, pageSize: 1 },
    },
    {
      select: (data) => {
        const tags = (data.options as any)?.tags || [];
        // Extract locales from browser_locale:XX tags
        const locales = new Set<string>();
        tags.forEach((tag: string) => {
          if (tag.startsWith('browser_locale:')) {
            const locale = tag.split(':')[1];
            if (locale) locales.add(locale);
          }
        });

        return Array.from(locales)
          .sort()
          .map((locale) => ({
            label: locale,
            value: locale,
          }));
      },
    },
  );

  return {
    options: data || [],
    loading: isLoading,
    error: !!error,
  };
}

/**
 * Hook to get distinct UTM sources from customer tags
 * Extracts sources from utm_source:XX tags
 */
export function useUtmSourceFilterOptions() {
  const { data, isLoading, error } = trpc.customers.list.useQuery(
    {
      pagination: { page: 1, pageSize: 1 },
    },
    {
      select: (data) => {
        const tags = (data.options as any)?.tags || [];
        // Extract UTM sources from utm_source:XX tags
        const sources = new Set<string>();
        tags.forEach((tag: string) => {
          if (tag.startsWith('utm_source:')) {
            const source = tag.split(':')[1];
            if (source) sources.add(source);
          }
        });

        return Array.from(sources)
          .sort()
          .map((source) => ({
            label: source.charAt(0).toUpperCase() + source.slice(1),
            value: source,
          }));
      },
    },
  );

  return {
    options: data || [],
    loading: isLoading,
    error: !!error,
  };
}
